<template>
  <div>
    <Loader :messageSelector="messageSelector" :overlay="overlay" />
    <DemandeConnexion
      :affiche="afficheDialog"
      :isConnexionLara="false"
      :isRedirectXtranet="true"
      :texte="genererTexteContactExistant"
      @close="closeDialog"
    ></DemandeConnexion>

    <v-form ref="form" v-model="valid" :disabled="readonly">
      <fieldset>
        <legend class="text-h6 text-sm-h5">Identification et coordonnées</legend>
        <div>
          <RessourceInfoBulle code="AI-RG-1" />

          <ContactDeBase
            key="renseignementsGeneraux"
            ref="renseignementsGeneraux"
            :renseignementsGeneraux="renseignementsGeneraux"
            :rules="rules"
            @validate-form="validateForm"
          ></ContactDeBase>
          <!-- Telephone -->
          <v-phone-input v-model="renseignementsGeneraux.telephoneResidence" :rules="rules.telephone" label="Téléphone résidentiel"></v-phone-input>
          <!-- Genre -->
          <v-container fluid>
            <p class="cssRequired">À quel genre vous identifiez-vous ?</p>
            <v-radio-group v-model="renseignementsGeneraux.genre" :rules="rules.genre" class="mt-3" required>
              <v-radio v-for="genre in listeGenres" :key="genre.id" :label="`${genre.lang00}`" :value="genre"></v-radio>
            </v-radio-group>
          </v-container>
          <!-- Sexe -->
          <v-container fluid>
            <p class="cssRequired">Indiquez votre sexe à la naissance</p>
            <v-radio-group v-model="renseignementsGeneraux.sexe" :rules="rules.sexe" class="mt-3" required>
              <v-radio v-for="sexe in listeSexes" :key="sexe.id" :label="`${sexe.lang00}`" :value="sexe"></v-radio>
            </v-radio-group>
            <p class="mb-6 warning-text">
              *Requis pour la validation de votre identité par la Régie de l'assurance maladie du Québec, notamment afin d'autoriser l'accès des
              membres au registre des intervenants.
            </p>
          </v-container>
          <!-- Citoyennete -->
          <v-autocomplete
            v-model="renseignementsGeneraux.citoyennetes"
            :item-text="itemText"
            :item-value="itemValue"
            :items="listeCitoyennete"
            :rules="rules.citoyennetes"
            class="cssRequired"
            label="Citoyenneté(s)"
            multiple
            required
            return-object
            @change="modelChange()"
          ></v-autocomplete>
          <!-- Autre Citoyennete -->
          <v-text-field
            v-if="autreCitoyennete"
            v-model="renseignementsGeneraux.autresCitoyennetes"
            class="cssRequired"
            label="Autre citoyenneté"
            required
          >
          </v-text-field>

          <!-- Pays de naissance -->
          <v-autocomplete
            v-model="renseignementsGeneraux.paysDeNaissance"
            :item-text="itemText"
            :item-value="itemValue"
            :items="listePaysDeNaissance"
            :rules="rules.paysDeNaissance"
            class="cssRequired"
            label="Pays de naissance"
            required
            return-object
            @change="modelChange()"
          ></v-autocomplete>

          <!-- Autre Pays de naissance -->
          <v-text-field v-if="autrePays" v-model="renseignementsGeneraux.autrePays" class="cssRequired" label="Autre pays" required></v-text-field>
        </div>
      </fieldset>

      <fieldset>
        <legend class="text-h6 text-sm-h5">Preuves d'identité</legend>
        <Attachment
          ref="identityAttachment"
          file-prefix="identité"
          :hide-title="true"
          @completeEvent="saveNextAttachment"
          @refreshEvent="validateForm(true)"
        ></Attachment>
      </fieldset>
    </v-form>

    <div class="mt-6 d-flex justify-space-between">
      <v-btn :disabled="!valid" color="secondary" @click="saveAndContinueLater()">Enregistrer et terminer plus tard </v-btn>
      <v-btn :disabled="!valid" color="primary" @click="saveAndContinue()">Suivant</v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Attachment from "../attachments/Attachment";
import ContactDeBase from "../common/ContactDeBase";
import DemandeConnexion from "../common/DemandeConnexion";
import Loader from "../Loader";
import NavigationPageFormulaireMixin from "@/mixins/navigationPageFormulaireMixin";
import PageReadonlyFormulaireAdmissionMixin from "@/mixins/pageReadonlyFormulaireAdmissionMixin";
import AttachementFormulaireMixin from "@/mixins/attachementFormulaireMixin";

export default {
  mixins: [NavigationPageFormulaireMixin, PageReadonlyFormulaireAdmissionMixin, AttachementFormulaireMixin],
  components: {
    Attachment: Attachment,
    ContactDeBase: ContactDeBase,
    Loader: Loader,
    DemandeConnexion,
  },
  props: {
    routeName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      overlay: false,
      messageSelector: "",
      rules: {
        firstName: [(v) => !!v || "Le prénom est requis"],
        lastName: [(v) => !!v || "Le nom de famille est requis"],
        birthday: [(v) => !!v || "La date de naissance est requise"],
        sexe: [(v) => !!v || "Le sexe est requis"],
        genre: [(v) => !!v || "Le genre est requis"],
        dateDeNaissance: [(v) => !!v || "La date de naissance est requise"],
        citoyennetes: [(v) => (!!v && v.length != 0) || "La citoyenneté est requise"],
        paysDeNaissance: [(v) => (!!v && v.length != 0) || "Le pays de naissance est requis"],
        adresse: [(v) => !!v || "L'adresse est requise"],
        ville: [(v) => !!v || "La ville est requise"],
        provinceOuEtatSiApplicable: [(v) => !this.isProvinceRequired || (!!v && v.length != 0) || "La province ou l'état est requis"],
        pays: [(v) => (!!v && v.length != 0) || "Le pays est requis"],
        postalCode: [(v) => !!v || "Le code postal est requis"],
        telephone: [
          (v) =>
            (this.renseignementsGeneraux.cellulaire && this.renseignementsGeneraux.cellulaire.length > 0) ||
            (this.renseignementsGeneraux.telephoneResidence && this.renseignementsGeneraux.telephoneResidence.length > 0) ||
            "Au moins un numéro de téléphone est requis",
        ],
        cellulaire: [
          (v) =>
            (this.renseignementsGeneraux.cellulaire && this.renseignementsGeneraux.cellulaire.length > 0) ||
            (this.renseignementsGeneraux.telephoneResidence && this.renseignementsGeneraux.telephoneResidence.length > 0) ||
            "Cette valeur est requise",
        ],
        email: [(v) => !!v || "Le courriel est requis", (v) => this.validateEmail || "Le format du courriel est invalide"],
      },
      valid: true,
      afficheDialog: false,
    };
  },
  created() {
    this.fetchItems();
  },
  computed: {
    ...mapGetters({
      demandeAdmissionId: "DemandeAdmission/GET_DEMANDEADMISSIONID",
      demandeAdmission: "DemandeAdmission/GET_DEMANDEADMISSION",
      renseignementsGeneraux: "RenseignementsGeneraux/getRenseignementsGeneraux",
      listePaysDeNaissance: "Catalogue/GET_PAYSDENAISSANCE",
      listeCitoyennete: "Catalogue/GET_CITOYENNETE",
      listeSexes: "Catalogue/GET_SEXE",
      listeGenres: "Catalogue/getGenre",
      contactExiste: "Contact/GET_CONTACTEXISTE",
    }),
    autrePays() {
      return this.renseignementsGeneraux.paysDeNaissance && this.renseignementsGeneraux.paysDeNaissance.id == 8168;
    },
    autreCitoyennete() {
      return this.renseignementsGeneraux.citoyennetes && this.renseignementsGeneraux.citoyennetes.find((c) => c.id == 8172);
    },
    validateEmail() {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(this.renseignementsGeneraux.courriel);
    },
    genererTexteContactExistant() {
      return "Vous êtes déjà enregistré.<br/> Veuillez vous connecter à votre compte.";
    },
    isProvinceRequired() {
      if (this.renseignementsGeneraux.pays) {
        return (
          (this.renseignementsGeneraux.pays.id === this.$catalogues.pays.canada ||
            this.renseignementsGeneraux.pays.id === this.$catalogues.pays.france ||
            this.renseignementsGeneraux.pays.id === this.$catalogues.pays.usa) &&
          this.renseignementsGeneraux.provinceOuEtatSiApplicable == null
        );
      } else {
        return false;
      }
    },
  },
  methods: {
    ...mapMutations({
      setSexe: "RenseignementsGeneraux/setSexe",
      setGenre: "RenseignementsGeneraux/setGenre",
      setDemandeAdmissionId: "DemandeAdmission/SET_ID",
    }),
    ...mapActions({
      getCatalogue: "Catalogue/getCatalogue",
      fetchDemandeAdmission: "DemandeAdmission/fetchDemandeAdmission",
      getRenseignementsGenerauxFromAdmission: "RenseignementsGeneraux/getRenseignementsGenerauxFromAdmission",
      updateDemandeAdmission: "DemandeAdmission/updateDemandeAdmission",
      updateRenseignementsGenerauxFromAdmission: "RenseignementsGeneraux/updateRenseignementsGenerauxFromAdmission",
      createDemandeAdmission: "DemandeAdmission/createDemandeAdmission",
      getDocumentsByDemandeAdmissionId: "Documents/getDocumentsByDemandeAdmissionId",
      methodContactExiste: "Contact/existe",
    }),
    async fetchItems() {
      try {
        this.messageSelector = "Loading";
        this.overlay = true;

        const requests = [
          this.getCatalogue(this.$cataloguesDesc.paysOrigine),
          this.getCatalogue(this.$cataloguesDesc.citoyennete),
          this.getCatalogue(this.$cataloguesDesc.sexe),
          this.getCatalogue(this.$cataloguesDesc.genre),
        ];

        // Chargement des id de contexte depuis l'URL
        if (this.$route.query.demandeAdmissionId != null) {
          this.setDemandeAdmissionId(this.$route.query.demandeAdmissionId);
        }

        if (this.demandeAdmissionId && this.demandeAdmissionId != "") {
          requests.push(this.fetchDemandeAdmission(this.demandeAdmissionId));
          requests.push(this.getRenseignementsGenerauxFromAdmission(this.demandeAdmissionId));
          requests.push(this.getDocumentsByDemandeAdmissionId(this.demandeAdmissionId));

          await Promise.all(requests);

          // hack pour l'affichage du radio
          this.setSexe(this.listeSexes.find((s) => s.id === this.renseignementsGeneraux?.sexe?.id));
          this.setGenre(this.listeGenres.find((s) => s.id === this.renseignementsGeneraux?.genre?.id));
          this.$refs.form.resetValidation();

          this.validateForm();
        }

        // Liste les uploads de fichiers
        this.refsUpload = [() => this.$refs.identityAttachment];

        this.overlay = false;
      } catch (erreur) {
        console.log(erreur);
      }
    },
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    saveAndContinueLater() {
      this.redirectValue = "later";

      if (this.readonly) {
        return this.redirect();
      }

      this.demandeAdmission.EnvoiLien = true;
      this.save();
    },
    async save() {
      try {
        this.validateForm();
        if (this.valid) {
          this.messageSelector = "Creation";
          this.overlay = true;
          if (!this.demandeAdmissionId || this.demandeAdmissionId == "") {
            await this.methodContactExiste(this.renseignementsGeneraux);
            if (this.contactExiste) {
              this.afficheDialog = true;
            }
          }

          if (this.contactExiste == false) {
            this.demandeAdmission.origineAdmission.id = this.$catalogues.origineDemandeAdmission.admission;
            this.demandeAdmission.creationDuContact = false;

            if (this.demandeAdmissionId && this.demandeAdmissionId != "") {
              this.demandeAdmission.id = this.demandeAdmissionId;
              await this.updateDemandeAdmission(this.demandeAdmission);
              await this.updateRenseignementsGenerauxFromAdmission({
                id: this.demandeAdmissionId,
                ...this.renseignementsGeneraux,
              });
            } else {
              await this.createDemandeAdmission({
                id: this.demandeAdmissionId,
                ...this.renseignementsGeneraux,
                ...this.demandeAdmission,
              });
            }

            this.saveAttachments("demandeAdmission", this.demandeAdmissionId, () => {
              this.overlay = false;
              this.redirect();
            });
          }
        }
        this.overlay = false;
      } catch (ex) {
        console.log(ex);
        this.overlay = false;
      }
    },
    redirect() {
      if (this.redirectValue === "later") {
        this.$router.push({ name: "TerminerPlusTard" });
      } else if (this.redirectValue === "continue") {
        this.$router.replace({ query: { ...this.$route.query, demandeAdmissionId: this.demandeAdmissionId } });
        this.next();
      }
    },
    closeDialog() {
      this.afficheDialog = false;
    },
    validateForm(resetValidation = false) {
      this.valid = this.$refs.form?.validate() || false;

      if (resetValidation) {
        this.$refs.form?.resetValidation();
      }
    },
    modelChange() {
      this.$nextTick(() => {
        this.model = null;
      });
      this.validateForm();
    },
  },
};
</script>

<style></style>